// VivaAssess Theme Context
// "Where Conversations Become Credible Assessments"

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Theme } from '../types';
import { DEFAULT_ACCENT_COLOR } from '../lib/settingsDefaults';
import { useTranslation } from 'react-i18next';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
  resetThemePreferences: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    const saved = localStorage.getItem('vivaassess-theme');
    return (saved as Theme) || defaultTheme;
  });

  const [accentColor, setAccentColorState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_ACCENT_COLOR;
    return localStorage.getItem('vivaassess-accent') || DEFAULT_ACCENT_COLOR;
  });

  const [isDark, setIsDark] = useState(false);

  // Determine if dark mode should be active
  useEffect(() => {
    const updateDarkMode = () => {
      let shouldBeDark = false;

      if (theme === 'dark') {
        shouldBeDark = true;
      } else if (theme === 'light') {
        shouldBeDark = false;
      } else {
        // System preference
        shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }

      setIsDark(shouldBeDark);

      // Update document class
      if (shouldBeDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    updateDarkMode();

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        updateDarkMode();
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Persist theme preference
  useEffect(() => {
    localStorage.setItem('vivaassess-theme', theme);
  }, [theme]);

  // Persist accent color
  useEffect(() => {
    localStorage.setItem('vivaassess-accent', accentColor);
    document.documentElement.style.setProperty('--accent-color', accentColor);
  }, [accentColor]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState((current: Theme) => {
      if (current === 'light') return 'dark';
      if (current === 'dark') return 'light';
      return isDark ? 'light' : 'dark';
    });
  };

  const setAccentColor = (color: string) => {
    setAccentColorState(color);
  };

  const resetThemePreferences = () => {
    setThemeState('system');
    setAccentColorState(DEFAULT_ACCENT_COLOR);
    if (typeof window !== 'undefined') {
      localStorage.setItem('vivaassess-theme', 'system');
      localStorage.setItem('vivaassess-accent', DEFAULT_ACCENT_COLOR);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDark,
        setTheme,
        toggleTheme,
        accentColor,
        setAccentColor,
        resetThemePreferences
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Theme toggle button component
export function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation('appearance');

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <button
        onClick={() => setTheme('light')}
        className={`p-2 rounded-lg transition-colors ${
          theme === 'light'
            ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200'
            : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        title={t('light')}
        aria-label={t('light')}
      >
        <SunIcon className="h-5 w-5" />
      </button>
      
      <button
        onClick={() => setTheme('dark')}
        className={`p-2 rounded-lg transition-colors ${
          theme === 'dark'
            ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200'
            : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        title={t('dark')}
        aria-label={t('dark')}
      >
        <MoonIcon className="h-5 w-5" />
      </button>
      
      <button
        onClick={() => setTheme('system')}
        className={`p-2 rounded-lg transition-colors ${
          theme === 'system'
            ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200'
            : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        title={t('system')}
        aria-label={t('system')}
      >
        <ComputerIcon className="h-5 w-5" />
      </button>
    </div>
  );
}

// Simple theme toggle button (single button)
export function SimpleThemeToggle({
  className = '',
  showLabel = false
}: {
  className?: string;
  showLabel?: boolean;
}) {
  const { toggleTheme, isDark } = useTheme();
  const { t } = useTranslation('appearance');
  const nextMode = isDark ? 'light' : 'dark';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={[
        'inline-flex items-center justify-center gap-2 rounded-xl border-2 px-2.5 py-2 text-sm font-semibold shadow-sm transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
        isDark
          ? 'border-amber-400/70 bg-slate-900 text-amber-200 hover:bg-slate-800 hover:text-amber-100'
          : 'border-slate-200 bg-white text-primary-800 hover:bg-primary-50 hover:text-primary-950',
        className
      ].filter(Boolean).join(' ')}
      title={t('switchMode', { mode: t(nextMode) })}
      aria-label={t('currentMode', { current: t(isDark ? 'dark' : 'light'), next: t(nextMode) })}
    >
      {isDark ? <SunIcon className="h-5 w-5 shrink-0" /> : <MoonIcon className="h-5 w-5 shrink-0" />}
      {showLabel && (
        <span className="hidden whitespace-nowrap text-xs font-bold uppercase tracking-wide sm:inline">
          {t(nextMode)}
        </span>
      )}
    </button>
  );
}

// Icon components
function SunIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function MoonIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}

function ComputerIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}
